import {FC, MouseEvent, useMemo} from 'react';
import {Box, Button, Divider, IconButton, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {AiAgentSession} from 'src/types';
import {groupSessionsByDate} from '../utils/groupSessionsByDate';
import {sidebarSx} from './SessionSidebar.sx';

interface SessionSidebarProps {
    sessions: AiAgentSession[];
    activeSessionId: string | null;
    onSelectSession: (sessionId: string) => void;
    onNewChat: () => void;
    onDeleteSession: (sessionId: string) => void;
    disableNewChat?: boolean;
}

export const SessionSidebar: FC<SessionSidebarProps> = ({
    sessions,
    activeSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    disableNewChat,
}) => {
    const intl = useIntl();

    const groups = useMemo(() => groupSessionsByDate(sessions, {
        today: intl.formatMessage({id: 'aiAgentToday'}),
        yesterday: intl.formatMessage({id: 'aiAgentYesterday'}),
        prev7: intl.formatMessage({id: 'aiAgentPrevious7Days'}),
        prev30: intl.formatMessage({id: 'aiAgentPrevious30Days'}),
    }), [sessions, intl]);

    const handleDelete = (e: MouseEvent, sessionId: string) => {
        e.stopPropagation();
        onDeleteSession(sessionId);
    };

    return (
        <Box sx={sidebarSx.root}>
            <Box sx={sidebarSx.header}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onNewChat}
                    disabled={disableNewChat}
                    fullWidth
                    disableElevation
                    sx={sidebarSx.newChatButton}
                >
                    {intl.formatMessage({id: 'aiAgentNewChat'})}
                </Button>
            </Box>
            <Divider sx={sidebarSx.divider} />
            <Box sx={sidebarSx.list}>
                {groups.length === 0 && (
                    <Typography sx={sidebarSx.emptyText}>
                        {intl.formatMessage({id: 'aiAgentDescription'})}
                    </Typography>
                )}
                {groups.map((group) => (
                    <Box key={group.label}>
                        <Typography sx={sidebarSx.groupLabel}>
                            {group.label}
                        </Typography>
                        {group.sessions.map((session) => (
                            <Box
                                key={session._id}
                                sx={session._id === activeSessionId ? sidebarSx.sessionItemActive : sidebarSx.sessionItem}
                                onClick={() => onSelectSession(session._id)}
                            >
                                <Box sx={sidebarSx.sessionTitleRow}>
                                    <Typography sx={sidebarSx.sessionTitle}>
                                        {session.title || session.greeting || intl.formatMessage({id: 'aiAgentDefaultChatTitle'})}
                                    </Typography>
                                    <IconButton
                                        className="delete-btn"
                                        size="small"
                                        sx={sidebarSx.deleteButton}
                                        onClick={(e) => handleDelete(e, session._id)}
                                    >
                                        <DeleteOutlineIcon sx={{fontSize: 16}} />
                                    </IconButton>
                                </Box>
                                <Box sx={sidebarSx.sessionMeta}>
                                    <Typography component="span" sx={sidebarSx.sessionDate}>
                                        {dayjs(session.last_active || session.created_at).format('HH:mm')}
                                    </Typography>
                                    {(session.message_count ?? 0) > 0 && (
                                        <Typography component="span" sx={sidebarSx.sessionDate}>
                                            {' · '}{session.message_count} {intl.formatMessage({id: 'aiAgentMessages'})}
                                        </Typography>
                                    )}
                                </Box>
                                {session.last_message_preview && (
                                    <Typography sx={sidebarSx.sessionPreview}>
                                        {session.last_message_preview}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};